// Auswahl-Logik des Explorers — Windows-Konventionen, an die Web-Realität
// angepasst.
//
// Der Nutzer will einen EINFACHKLICK zum Öffnen (das war der Auslöser des
// Umbaus). Damit kollidiert die klassische Mehrfachauswahl, bei der ein
// blanker Klick nur markiert. Auflösung:
//   - blanker Klick        → Auswahl auf genau diese Zeile + ÖFFNEN
//   - Strg/Cmd + Klick     → Auswahl togglen, NICHT öffnen
//   - Umschalt + Klick     → Bereich ab Anker, NICHT öffnen
//   - Rechtsklick          → markiert die Zeile, falls sie noch nicht Teil der
//                            Auswahl ist (sonst bleibt die Mehrfachauswahl
//                            stehen, damit Sammel-Aktionen greifen)
// Zusätzlich gibt es eine Checkbox-Spalte, sonst wäre Mehrfachauswahl auf
// Touch-Geräten unerreichbar.

import * as React from "react";
import { rangeBetween } from "./explorerModel";
import { aktiveAuswahl } from "../../windowManager/browserFenster/fensterRegister";

export type ExplorerSelection = {
  selected: ReadonlySet<string>;
  isSelected: (key: string) => boolean;
  /** Klick auf eine Zeile. Liefert true, wenn die Zeile geöffnet werden soll. */
  handleRowClick: (key: string, event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => boolean;
  /** Rechtsklick: markiert die Zeile, falls sie nicht Teil der Auswahl ist. */
  handleContextMenu: (key: string) => void;
  /** Checkbox: togglet ohne Öffnen. */
  toggle: (key: string) => void;
  selectAll: () => void;
  clear: () => void;
  /** Auswahl auf die aktuell sichtbaren Zeilen eindampfen (nach Ordnerwechsel). */
  retainOnly: (keys: string[]) => void;
};

export const useExplorerSelection = (orderedKeys: string[]): ExplorerSelection => {
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(() => new Set());
  const anchor = React.useRef<string | null>(null);

  // `orderedKeys` ändert sich bei jedem Render der Liste; als Ref gehalten,
  // damit die Callbacks stabil bleiben und die DataTable nicht durchrendert.
  const keysRef = React.useRef(orderedKeys);
  keysRef.current = orderedKeys;

  const isSelected = React.useCallback((key: string) => selected.has(key), [selected]);

  const toggle = React.useCallback((key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    anchor.current = key;
  }, []);

  const handleRowClick = React.useCallback(
    (key: string, event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }): boolean => {
      if (event.shiftKey) {
        const range = rangeBetween(keysRef.current, anchor.current ?? key, key);
        setSelected(new Set(range));
        // Die Text-Markierung, die der Browser beim Umschalt-Klick anlegt,
        // würde sonst quer über die Tabelle stehen bleiben.
        aktiveAuswahl()?.removeAllRanges();
        return false;
      }
      if (event.ctrlKey || event.metaKey) {
        toggle(key);
        return false;
      }
      setSelected(new Set([key]));
      anchor.current = key;
      return true;
    },
    [toggle],
  );

  const handleContextMenu = React.useCallback((key: string) => {
    setSelected((prev) => (prev.has(key) ? prev : new Set([key])));
    anchor.current = key;
  }, []);

  const selectAll = React.useCallback(() => {
    setSelected(new Set(keysRef.current));
  }, []);

  const clear = React.useCallback(() => {
    setSelected(new Set());
    anchor.current = null;
  }, []);

  const retainOnly = React.useCallback((keys: string[]) => {
    const allowed = new Set(keys);
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => allowed.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, []);

  return { selected, isSelected, handleRowClick, handleContextMenu, toggle, selectAll, clear, retainOnly };
};
